import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { DollarSign, Plus, Trash2 } from "lucide-react";

export default function AdminRevenue() {
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ user_id: "", submission_id: "", amount: "", period: "", notes: "" });
  const [userSubs, setUserSubs] = useState([]);

  const load = () => api.get("/admin/revenue").then((r) => setRecords(r.data));
  useEffect(() => {
    load();
    api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role !== "admin")));
  }, []);

  useEffect(() => {
    if (form.user_id) {
      api.get("/admin/submissions").then((r) => setUserSubs(r.data.filter((s) => s.user_id === form.user_id)));
    } else setUserSubs([]);
  }, [form.user_id]);

  const create = async () => {
    try {
      await api.post("/admin/revenue", { ...form, amount: parseFloat(form.amount || "0") });
      toast.success("Revenue added");
      setCreating(false); setForm({ user_id: "", submission_id: "", amount: "", period: "", notes: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/revenue/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const total = records.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight">Revenue</h1>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="add-revenue-btn"
          className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Add Revenue
        </Button>
      </div>

      <div className="mt-8 p-8 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/20 to-transparent">
        <DollarSign className="w-6 h-6 text-emerald-400 mb-4" />
        <div className="text-5xl font-bold gradient-text">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        <div className="mt-2 text-sm text-zinc-400 uppercase tracking-wider">Total Platform Revenue</div>
      </div>

      <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">Date</th>
              <th className="p-4">User</th>
              <th className="p-4">Period</th>
              <th className="p-4">Notes</th>
              <th className="p-4 text-right">Amount</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} data-testid={`admin-rev-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="p-4 text-zinc-400 text-sm">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-4">{r.user?.full_name}<br /><span className="text-xs text-zinc-500">{r.user?.email}</span></td>
                <td className="p-4 text-zinc-400 text-sm">{r.period || "—"}</td>
                <td className="p-4 text-zinc-400 text-sm">{r.notes || "—"}</td>
                <td className="p-4 text-right font-mono text-emerald-400 font-semibold">${Number(r.amount).toFixed(2)}</td>
                <td className="p-4 text-right">
                  <Button size="icon" variant="ghost" onClick={() => del(r.id)} className="h-8 w-8 text-zinc-400 hover:text-rose-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {records.length === 0 && <tr><td colSpan="6" className="p-12 text-center text-zinc-500">No revenue records yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="add-rev-dialog">
          <DialogHeader><DialogTitle>Add Revenue Entry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="User">
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v, submission_id: "" })}>
                <SelectTrigger data-testid="rev-user-select" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Choose user" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.email}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Song (optional)">
              <Select value={form.submission_id} onValueChange={(v) => setForm({ ...form, submission_id: v })} disabled={!form.user_id}>
                <SelectTrigger data-testid="rev-song-select" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="All / specific song" /></SelectTrigger>
                <SelectContent>{userSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.song_title}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Amount (USD)"><Input type="number" step="0.01" data-testid="rev-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-zinc-900 border-zinc-800" /></F>
            <F label="Period"><Input data-testid="rev-period" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="e.g. Jan 2026" className="bg-zinc-900 border-zinc-800" /></F>
            <F label="Notes"><Textarea rows={2} data-testid="rev-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-900 border-zinc-800" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={create} data-testid="rev-submit" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Add Revenue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function F({ label, children }) { return <div><Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label><div className="mt-1.5">{children}</div></div>; }
